import React, { useState } from "react";
import { map, get, find } from "lodash";
import { Modal, Popover, Spin, Table } from "antd";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { AlignType } from "rc-trigger/lib/interface";

// import local dependencies
import Icon from "../../../../assets/icon_config";
import { Percentage } from "./percentage";
import { Title } from "./title";
import DashboardService from "@services/dashboard.service";
import { routeMap } from "@utils/constants";
import { usePopulationInsightStore } from "@stores/populationInsightStore";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";

export const Reasons = (props: {
  style: AlignType | undefined;
  reasonsOptions: any;
  handleClick: () => void;
  visible: boolean | undefined;
  record: any;
  selectedFilterOptions: any;
  rangeStartDate: any;
  rangeEndDate: any;
  activeSideCard: any;
  explainabilityInfo: any;
  title: string;
}) => {

  const _isModalVisible = usePopulationInsightStore((state) => state.isReasonsModalVisible);
  const _setIsModalVisible = usePopulationInsightStore((state) => state.handleReasonsModalVisibility);
  const _reasonModalTitle = usePopulationInsightStore((state) => state.reasonModalTitle);
  const _setReasonModalTitle = usePopulationInsightStore((state) => state.setReasonModalTitle);

  const showModal = () => {
    _setIsModalVisible(true);
  };

  const handleCancel = () => {
    _setIsModalVisible(false);
  };

  return (
    <>
      <Popover
        align={props.style}
        title={props.title}
        overlayClassName="ui-population-insight__overlay"
        zIndex={1}
        content={
          <>
            <div className="ui-population-insight__reasons">
              {/* <div className="ui-population-insight__reasons__warning">
                <div className="ui-population-insight__reasons__warning__icon">
                  <Icon name="alert-minor" />
                </div>
                <div className="ui-population-insight__reasons__warning__title">
                  In descending order of the impact on the graph
                </div>
              </div> */}
              {map(props.reasonsOptions, (option) => {
                return (
                  <div
                    key={option.value}
                    className="ui-population-insight__reasons__option"
                    onClick={(e) => {
                      e.stopPropagation();
                      // props.handleClick();
                      _setReasonModalTitle(option.title);
                      showModal();
                    }}
                  >
                    <div className="ui-population-insight__reasons__option__title">{option.title}</div>
                    <div className="ui-population-insight__reasons__option__icon">
                      <Icon name={"arrowRight"} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        }
        visible={props.visible}
      ></Popover>

      <Modal title={_reasonModalTitle} visible={_isModalVisible} width={1215} footer={false} onCancel={handleCancel} zIndex={2}>
        <TableInfo
          record={props.record}
          selectedFilterOptions={props.selectedFilterOptions}
          rangeStartDate={props.rangeStartDate}
          rangeEndDate={props.rangeEndDate}
          title={_reasonModalTitle}
          activeSideCard={props.activeSideCard}
          explainabilityInfo={get(find(get(find(props.explainabilityInfo, { type: _reasonModalTitle }), "info", []), { yearMonth: get(props.record, "x", undefined) }), "data", [])}
        />
      </Modal>
    </>
  );
};

const TableInfo = (props: {
  selectedFilterOptions: any;
  title: any;
  activeSideCard: any;
  record: {
    x: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | null | undefined;
  };
  explainabilityInfo: readonly any[] | undefined;
  rangeStartDate: any;
  rangeEndDate: any;
}) => {
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const patientIdsHandler = usePopulationInsightStore((state) => state.patientIdsHandler);
  const memberPageNavigationHandler = usePopulationInsightStore((state) => state.memberPageNavigationHandler);

  const fetchPatientIDs = (code: any) => {
    DashboardService.fetchMemberListFromPopInst({ filters: props.selectedFilterOptions }, moment(get(props, "record.x", 0)).format("YYYY-MM"), props.title, code).subscribe({
      next: (v) => {
        patientIdsHandler(v.data.data.patientIds);
        memberPageNavigationHandler(true);
        navigate(get(routeMap, "members"), {
          state: {
            report: props.activeSideCard,
          },
        });
        setLoader(false);
      },
      error: (e) => {
        setLoader(false);
      },
    });
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      sorter: (a: any, b: any) => a.code - b.code,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "25%",
      className: "description",
    },
    {
      title: "Patient Count",
      children: [
        {
          title: "Previous Patient Count",
          dataIndex: "previousPatientCount",
          key: "previousPatientCount",
          sorter: (a: any, b: any) => a.previousPatientCount - b.previousPatientCount,
        },
        {
          title: "Patient Count",
          dataIndex: "patientCount",
          key: "patientCount",
          sorter: (a: any, b: any) => a.patientCount - b.patientCount,
          render: (text: any, row: any) => {
            return (
              <div
                className="ui-population-insight__link"
                onClick={() => {
                  setLoader(true);
                  fetchPatientIDs(get(row, "code", null));
                }}
              >
                {text}
              </div>
            );
          },
        },
        
      ],
    },
    {
      title: props.activeSideCard.reasonTitle,
      children: [
        
        {
          title: (
            <div>
              Previous Month <br></br> ({new Date(new Date(get(props, "record.x", "")).setMonth(new Date(get(props, "record.x", "")).getMonth() - 1)).toLocaleString("en-us", { month: "short" })}{" "}
              {new Date(new Date(get(props, "record.x", "")).setMonth(new Date(get(props, "record.x", "")).getMonth() - 1)).getFullYear()})
            </div>
          ),
          dataIndex: "previous",
          key: "previous",
          render: (text: any) => <Title suffix="$" text={text ? (text).toFixed(2).toLocaleString() : 0} />,
          sorter: (a: any, b: any) => a.previous - b.previous,
        },
        {
          title: (
            <div>
              Current Month <br></br> ({props.record.x})
            </div>
          ),
          dataIndex: "current",
          key: "current",
          render: (text: any) => <Title suffix="$" text={text ? (text).toFixed(2).toLocaleString() : 0} />,
          sorter: (a: any, b: any) => a.current - b.current,
        },       
        {
          title: "Change",
          dataIndex: "change",
          key: "change",
          render: (text: any) => <Percentage percentage={text} />,
          defaultSortOrder: 'ascend',
          sorter: (a: any, b: any) => a.change - b.change,
        },
      ],
    },
  ];

  return (
    <Spin spinning={loader} indicator={<HealthemSpinner/>}>
      <Table className="ui-population-insight__reasons__table" dataSource={props.explainabilityInfo} columns={columns} pagination={false} />
    </Spin>
  );
};
